import React, { useEffect, useRef, useState } from "react";
import {
  getSettings,
  updateSettings,
} from "../../../services/settingsServices";
import {
  uploadSettingsLogoClub,
  toAbsolutePhotoUrl,
} from "../../../services/settingsPhotoServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import { FaSave, FaTimes, FaUpload } from "react-icons/fa";

const SettingsForm = () => {
  const [settings, setSettings] = useState({
    nom_club: "",
    shortname_club: "",
    responsable_club: "",
    logo_club: "",
    adresse_club: "",
    commune_club: "",
    tel_club: "",
    mail_club: "",
    siteweb_club: "",
    siret_club: "",
    facebook_club: "",
    instagram_club: "",
    primary_color: "#4f46e5",
    autorisation_renouvellement: false,
    saison_active: "",
  });
  const [loading, setLoading] = useState(true);

  // Upload logo
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const uploadDialogRef = useRef(null);

  // Cache-busting (comme News)
  const withBust = (url, v) =>
    url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url;

  // Upload options (comme News)
  const [outputFormat, setOutputFormat] = useState("webp"); // webp|jpg|png
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imgVersion, setImgVersion] = useState(0);

  // Aperçu logo (depuis l’URL)
  const defaultLogo = "/img/no-image.jpg";

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();
        if (Array.isArray(settingsData) && settingsData.length > 0) {
          const first = settingsData[0];
          setSettings({
            ...first,
            primary_color: first?.primary_color || "#4f46e5",
          });

          // optionnel : force refresh image au chargement
          setImgVersion((v) => v + 1);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateSettings(settings);
      toast.success("Paramètres mis à jour avec succès.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour des paramètres.");
    }
  };

  // SAISON (ex: 2024-2025)
  const getSeasonOptions = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0=janvier
    const startYear = currentMonth >= 7 ? currentYear : currentYear - 1;
    return [
      `${startYear}-${startYear + 1}`,
      `${startYear + 1}-${startYear + 2}`,
    ];
  };

  // Upload modal handlers
  const openUploadModal = () => setIsUploadModalOpen(true);
  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setSelectedFile(null);
  };
  const handleFileChange = (e) => setSelectedFile(e.target.files[0]);

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const updated = await uploadSettingsLogoClub(selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const p = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(p);
        },
      });

      // updated.url = "/uploads/..." (thumb) ou https://...
      const newUrl = updated?.url || "";
      setSettings((prev) => ({ ...prev, logo_club: newUrl }));

      // force refresh image (cache-busting)
      setImgVersion((v) => v + 1);

      toast.success("Logo du club mis à jour !");
      closeUploadModal();
    } catch (error) {
      console.error("Erreur upload logo:", error?.response?.data || error);
      toast.error("Erreur lors de l'upload du logo.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // ESC pour fermer la modale d’upload + focus
  useEffect(() => {
    if (!isUploadModalOpen) return;
    const onEsc = (e) => e.key === "Escape" && closeUploadModal();
    window.addEventListener("keydown", onEsc);
    const t = setTimeout(() => {
      uploadDialogRef.current?.querySelector("input[type='file']")?.focus();
    }, 0);
    return () => {
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isUploadModalOpen]);

  const logoAbs = toAbsolutePhotoUrl(
    settings.logo_club?.trim() ? settings.logo_club : "",
  );
  const logoSrc = withBust(logoAbs || defaultLogo, imgVersion);

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-7 w-56 bg-white/20 rounded-md animate-pulse" />
            </div>
            <div className="p-6 space-y-3">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-12 rounded-xl bg-slate-100/80 dark:bg-slate-800/40 animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Paramètres de l’Association
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Identité, coordonnées, réseaux sociaux, saison et couleurs.
              </p>
            </div>
            <Button
              text="Enregistrer"
              icon={FaSave}
              onClick={handleSubmit}
              className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
            />
          </div>

          {/* CONTENT */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
            {/* Identité & Logo */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr,380px] gap-4 sm:gap-6">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Nom du club <span className="text-pink-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="nom_club"
                      value={settings.nom_club}
                      onChange={handleChange}
                      required
                      placeholder="Ex : Judo Club du Bouscat"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Nom raccourci <span className="text-pink-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="shortname_club"
                      value={settings.shortname_club}
                      onChange={handleChange}
                      required
                      placeholder="Ex : JCB"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Responsable du club
                    </label>
                    <input
                      type="text"
                      name="responsable_club"
                      value={settings.responsable_club}
                      onChange={handleChange}
                      placeholder="Ex : Nom Prénom"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Lien du logo
                    </label>
                    <input
                      type="url"
                      name="logo_club"
                      value={settings.logo_club}
                      onChange={handleChange}
                      placeholder="https://…/logo.png"
                      inputMode="url"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      PNG/SVG recommandé. Taille carrée conseillée (ex :
                      512×512).
                    </p>
                  </div>
                </div>
              </div>

              {/* Carte logo + upload */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5 flex flex-col items-center">
                <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden bg-white">
                  <img
                    src={logoSrc}
                    alt="Logo du club"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.src = defaultLogo;
                    }}
                  />
                </div>
                <Button
                  icon={FaUpload}
                  text="Changer le logo"
                  onClick={openUploadModal}
                  className="mt-4 !rounded-xl gap-2"
                />
              </div>
            </div>

            {/* Coordonnées */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Adresse
                    </label>
                    <input
                      type="text"
                      name="adresse_club"
                      value={settings.adresse_club}
                      onChange={handleChange}
                      placeholder="Rue et numéro"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Commune
                    </label>
                    <input
                      type="text"
                      name="commune_club"
                      value={settings.commune_club}
                      onChange={handleChange}
                      placeholder="Ex : Le Bouscat"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      name="tel_club"
                      value={settings.tel_club}
                      onChange={handleChange}
                      inputMode="tel"
                      placeholder="Ex : 05 56 XX XX XX"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Email
                    </label>
                    <input
                      type="email"
                      name="mail_club"
                      value={settings.mail_club}
                      onChange={handleChange}
                      placeholder="contact@club.fr"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      Site web
                    </label>
                    <input
                      type="url"
                      name="siteweb_club"
                      value={settings.siteweb_club}
                      onChange={handleChange}
                      placeholder="https://…"
                      inputMode="url"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      SIRET
                    </label>
                    <input
                      type="text"
                      name="siret_club"
                      value={settings.siret_club}
                      onChange={handleChange}
                      placeholder="Ex : 123 456 789 00012"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        Facebook
                      </label>
                      <input
                        type="url"
                        name="facebook_club"
                        value={settings.facebook_club}
                        onChange={handleChange}
                        placeholder="https://facebook.com/…"
                        inputMode="url"
                        className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        Instagram
                      </label>
                      <input
                        type="url"
                        name="instagram_club"
                        value={settings.instagram_club}
                        onChange={handleChange}
                        placeholder="https://instagram.com/…"
                        inputMode="url"
                        className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Apparence / Saison */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Couleur primaire
                </label>
                <div className="mt-2 flex items-center gap-3">
                  <input
                    type="color"
                    name="primary_color"
                    value={settings.primary_color || "#4f46e5"}
                    onChange={handleChange}
                    className="h-10 w-16 rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 cursor-pointer"
                    aria-label="Sélection de la couleur primaire"
                  />
                  <span
                    className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium"
                    style={{
                      backgroundColor: settings.primary_color || "#4f46e5",
                      color: "#fff",
                    }}
                  >
                    Aperçu
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Saison active
                </label>
                <select
                  name="saison_active"
                  value={settings.saison_active}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  {getSeasonOptions().map((season) => (
                    <option key={season} value={season}>
                      {season}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Autoriser le renouvellement (fin de saison)
                </label>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-sm text-slate-600 dark:text-slate-400 pr-4">
                    Si activé, les adhérents peuvent renouveler leur inscription
                    en fin de saison directement depuis l’espace public.
                  </p>
                  <input
                    type="checkbox"
                    name="autorisation_renouvellement"
                    checked={!!settings.autorisation_renouvellement}
                    onChange={handleChange}
                    className="toggle toggle-primary"
                    aria-label="Autoriser le renouvellement"
                  />
                </div>
              </div>
            </div>

            {/* Actions bas de page (en plus du bouton header) */}
            <div className="flex justify-end">
              <Button
                text="Mettre à jour"
                type="submit"
                icon={FaSave}
                className="!rounded-xl gap-2"
              />
            </div>
          </form>
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeUploadModal();
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Changer le logo du club"
        >
          <div
            ref={uploadDialogRef}
            className="relative w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95"
          >
            <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
              <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
                Changer le logo du club
              </h3>
              <button
                type="button"
                onClick={closeUploadModal}
                className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
                title="Fermer"
              >
                <FaTimes />
              </button>
            </div>

            <form
              onSubmit={handleUploadSubmit}
              className="p-4 sm:p-6 space-y-4"
            >
              <input
                type="file"
                onChange={handleFileChange}
                accept="image/png,image/jpeg,image/webp"
                className="w-full"
                disabled={isUploading}
              />

              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  Format
                </span>
                <select
                  className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  disabled={isUploading}
                >
                  <option value="webp">WEBP (recommandé)</option>
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                </select>
              </div>

              {isUploading && (
                <div>
                  <progress
                    className="progress w-full"
                    value={uploadProgress}
                    max="100"
                  />
                  <div className="text-xs mt-1">{uploadProgress}%</div>
                </div>
              )}

              {/* ✅ actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  disabled={isUploading}
                  className="rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Annuler"
                >
                  <FaTimes /> Fermer
                </button>

                <Button
                  text={isUploading ? "Transfert…" : "Sauvegarder"}
                  type="submit"
                  icon={FaSave}
                  className="!rounded-xl gap-2"
                  isLoading={isUploading}
                  // si ton Button n'a pas isLoading mais loading:
                  // loading={isUploading}
                  disabled={isUploading || !selectedFile}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Styles locaux */}
      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
};

export default SettingsForm;
